import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, Download, Loader2, RotateCcw, Send } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import { downloadBlob } from "../../../lib/downloadBlob";
import {
  LEVEL_WORDS, dayOf, lateStartLine, missingLine, modeLine, plusDays, reopenAllLabel, reopenAllQuestion,
  sendGroupLabel, sendGroupQuestion, stateLine, unsetWarning,
} from "../../../lib/initialAssessmentAdmin";
import ModeChoice from "./ModeChoice";
import GroupProfile from "../../assessment/GroupProfile";
import DeadlineDialog from "./DeadlineDialog";

export const TONE = {
  ok: "bg-[#519872]/15 text-[#2F6B4E]",
  wait: "bg-[#3F8EBA]/15 text-[#1f5d80]",
  bad: "bg-[#E63946]/15 text-[#B42330]",
  none: "bg-[#EDEEF0] text-[#6B7280]",
};

/**
 * A group's initial assessment, on its sheet (15.10.2026): how the group gets
 * it, who has answered and who is missing, the buttons to send and reopen,
 * the group's profile and the Excel for the dossier. Owner and admin only:
 * the answers are about people. Curs FPED groups have none, and show nothing.
 */
export default function GroupAssessmentPanel({ groupId, groupName }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // "send" | "reopen"
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/groups/${groupId}/initial-assessment`);
      setData(r.data);
      setMode(r.data?.mode?.mode || "");
      setNote(r.data?.mode?.note || "");
    } catch {
      setData({ failed: true });
    }
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <section className="text-sm text-[#6B7280] flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Evaluarea inițială…</section>;
  }
  if (data.failed || !data.applies) return null;

  const saved = data.mode || {};
  const dirty = mode !== (saved.mode || "") || (mode === "external" && note.trim() !== (saved.note || ""));
  const counts = data.counts || {};

  const saveMode = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/groups/${groupId}/initial-assessment`, { mode, note });
      toast.success("Am salvat cum primește grupa evaluarea inițială.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva alegerea."));
    } finally {
      setBusy(false);
    }
  };

  const run = async (day) => {
    const kind = dialog;
    setBusy(true);
    try {
      const r = await api.post(`/admin/groups/${groupId}/initial-assessment/${kind}`, { deadline: day });
      toast.success(kind === "send"
        ? `Am trimis chestionarul către ${r.data.sent} cursanți.`
        : `Am redeschis chestionarul pentru ${r.data.reopened} cursanți.`);
      setDialog(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut trimite chestionarul."));
    } finally {
      setBusy(false);
    }
  };

  const exportXlsx = () => downloadBlob(`/admin/groups/${groupId}/initial-assessment/export`,
    `Evaluare initiala - ${groupName || groupId}.xlsx`, setExporting);

  // Automat sends by itself; the button is for the groups the team sends to.
  const canSend = saved.mode === "manual";
  const profile = data.profile || {};

  return (
    <section className="rounded-2xl border border-[#E5E7EB] p-4" data-testid="group-assessment-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7]">
          <ClipboardList size={14} /> Evaluarea inițială: <span className="normal-case tracking-normal text-sm text-[#373A45]">{modeLine(saved)}</span>
        </h3>
        <button type="button" onClick={exportXlsx} disabled={exporting}
          className="inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] px-3 py-1 text-xs font-semibold text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-50"
          data-testid="group-assessment-export">
          {exporting ? <Loader2 className="animate-spin" size={12} /> : <Download size={12} />} Excel
        </button>
      </div>

      {!saved.mode && data.first_session && (
        <p className="mt-2 rounded-lg bg-[#FDF6E3] px-3 py-2 text-sm text-[#8a6a1a]" data-testid="group-assessment-unset">
          {unsetWarning(data.first_session)}
        </p>
      )}

      <div className="mt-3">
        <ModeChoice value={mode} note={note} onChange={setMode} onNote={setNote} disabled={busy}
          name={`assessment-mode-${groupId}`} />
        {dirty && mode && (
          <button type="button" onClick={saveMode} disabled={busy}
            className="mt-2 rounded-full bg-[#965EA7] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50"
            data-testid="group-assessment-save-mode">
            Salvează alegerea
          </button>
        )}
      </div>

      {saved.mode !== "external" && (
        <>
          <div className="mt-4 flex flex-wrap gap-2 text-xs" data-testid="group-assessment-counts">
            <Count label="Completat" n={counts.completed} tone="ok" />
            <Count label="Trimis, necompletat" n={counts.open} tone="wait" />
            <Count label="Închis, necompletat" n={counts.closed} tone="bad" />
            <Count label="Netrimis" n={counts.not_sent} tone="none" />
          </div>
          {counts.people > 0 && counts.completed < counts.people && (
            <p className="mt-2 text-sm text-[#373A45]">{missingLine(counts)}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {canSend && counts.not_sent > 0 && (
              <button type="button" onClick={() => setDialog("send")}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#7A4986]"
                data-testid="group-assessment-send">
                <Send size={12} /> {sendGroupLabel(counts.not_sent)}
              </button>
            )}
            {counts.closed > 0 && (
              <button type="button" onClick={() => setDialog("reopen")}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#965EA7] px-3 py-1.5 text-xs font-semibold text-[#7A4986] hover:bg-[#F0E7F4]"
                data-testid="group-assessment-reopen-all">
                <RotateCcw size={12} /> {reopenAllLabel(counts.closed)}
              </button>
            )}
          </div>

          {(data.rows || []).length > 0 && (
            <ul className="mt-3 divide-y divide-[#E5E7EB] rounded-xl border border-[#E5E7EB] text-sm" data-testid="group-assessment-rows">
              {data.rows.map((row) => {
                const line = stateLine(row.state, row);
                return (
                  <li key={row.enrollment_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                    <Link to={`/admin/enrollments?e=${row.enrollment_id}`} className="font-semibold text-[#373A45] hover:text-[#7A4986] hover:underline">
                      {row.name || "—"}
                    </Link>
                    <span className="flex flex-wrap items-center gap-2">
                      {row.request_at && <span className="text-[11px] font-semibold text-[#8a6a1a]">cere redeschiderea</span>}
                      {row.level && <span className="text-[11px] text-[#6B7280]">{LEVEL_WORDS[row.level]} <span className="italic">(orientativ)</span></span>}
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[line.tone]}`}>{line.text}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          {profile.answered > 0 && <GroupProfile profile={profile} texts={data.texts || []} />}
        </>
      )}

      <DeadlineDialog
        open={dialog === "send"}
        title="Trimiți chestionarul?"
        question={(day) => sendGroupQuestion(counts.not_sent, day)}
        note={data.late_start ? lateStartLine(data.deadline) : ""}
        initialDay={dayOf(data.deadline)}
        confirmLabel="Trimite"
        busy={busy}
        onConfirm={run}
        onClose={() => setDialog(null)}
      />
      <DeadlineDialog
        open={dialog === "reopen"}
        title="Redeschizi chestionarul?"
        question={(day) => reopenAllQuestion(counts.closed, day)}
        initialDay={plusDays(7)}
        confirmLabel="Redeschide și trimite"
        busy={busy}
        onConfirm={run}
        onClose={() => setDialog(null)}
      />
    </section>
  );
}

function Count({ label, n, tone }) {
  return (
    <span className={`rounded-full px-2.5 py-1 font-semibold ${TONE[tone]}`}>
      {label}: <span className="tabular-nums">{n || 0}</span>
    </span>
  );
}
